import { request, requestForm, requestNoContent } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/**
 * Lĩnh vực của dự án tiêu biểu — khớp đúng hai nhóm trên trang mvalab.vn/du-an-tieu-bieu.
 *
 * Số khớp với kiểu liệt kê ở máy chủ. Website cũ dùng mã chữ `image-processing` và `pc-control`.
 */
export const ShowcaseProjectCategory = {
  ComputerVision: 1,
  PcControl: 2,
} as const;

export type ShowcaseProjectCategoryValue =
  (typeof ShowcaseProjectCategory)[keyof typeof ShowcaseProjectCategory];

/** Tên hiển thị của lĩnh vực — đúng chữ website đang in trên thẻ. */
export const showcaseProjectCategoryNames: Readonly<Record<ShowcaseProjectCategoryValue, string>> = {
  1: 'Computer Vision',
  2: 'PC Control',
};

/** Tình trạng của dự án tiêu biểu. Website cũ dùng mã chữ `ongoing` và `completed`. */
export const ShowcaseProjectStatus = {
  Ongoing: 1,
  Completed: 2,
} as const;

export type ShowcaseProjectStatusValue =
  (typeof ShowcaseProjectStatus)[keyof typeof ShowcaseProjectStatus];

/** Tên hiển thị của tình trạng — đúng chữ website đang in trên thẻ. */
export const showcaseProjectStatusNames: Readonly<Record<ShowcaseProjectStatusValue, string>> = {
  1: 'Đang thực hiện',
  2: 'Hoàn thành',
};

/** Một dự án tiêu biểu. */
export interface ShowcaseProject {
  readonly id: string;
  readonly title: string;
  readonly clientName: string;
  readonly category: ShowcaseProjectCategoryValue;
  readonly status: ShowcaseProjectStatusValue;
  /** Ngày bắt đầu, dạng YYYY-MM-DD — ngày hiện trên thẻ của website. */
  readonly startDate: string;
  readonly endDate: string | null;
  readonly progressPercent: number;
  readonly description: string;
  /** Theo thứ tự hiển thị: thẻ trên website chỉ hiện bốn mục đầu kèm "+N". */
  readonly technologies: readonly string[];
  /** Theo thứ tự hiển thị: thẻ trên website chỉ hiện hai mục đầu. */
  readonly achievements: readonly string[];
  readonly imageUrl: string | null;
  /** Địa chỉ tệp demo trong kho (tên miền r2.dev) — Zalo chặn, không gửi link này cho khách. */
  readonly demoUrl: string | null;
  /** Đường dẫn của link gửi khách, ví dụ `he-thong-kiem-tra-pcb`. */
  readonly demoSlug: string | null;
  /** Link gửi khách trên tên miền công ty, ví dụ `https://demo.mvalab.vn/he-thong-kiem-tra-pcb`. */
  readonly demoShareUrl: string | null;
  readonly isPublished: boolean;
  readonly sortOrder: number;
  /** Số thứ tự trên website cũ, khi dự án được nhập từ đó. */
  readonly legacyWebsiteNumber: number | null;
  readonly updatedAt: string;
}

/** Điều kiện lọc danh sách. */
export interface ShowcaseProjectFilter {
  readonly searchTerm?: string;
  readonly category?: ShowcaseProjectCategoryValue;
  readonly status?: ShowcaseProjectStatusValue;
  /** Ba trạng thái: đang hiện, đang ẩn, hoặc không lọc (bỏ trống). */
  readonly isPublished?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung khi khai mới hoặc sửa. */
export interface SaveShowcaseProjectPayload {
  readonly title: string;
  readonly clientName: string;
  readonly category: ShowcaseProjectCategoryValue;
  readonly status: ShowcaseProjectStatusValue;
  readonly startDate: string;
  readonly endDate: string | null;
  readonly progressPercent: number;
  readonly description: string;
  readonly technologies: readonly string[];
  readonly achievements: readonly string[];
  readonly imageUrl: string | null;
  readonly demoUrl: string | null;
  readonly isPublished: boolean;
  readonly sortOrder: number;
  /** Để trống thì giữ đường dẫn đang có, hoặc tự tạo từ tên dự án khi có demo. */
  readonly demoSlug: string | null;
}

const route = '/api/v1/projects/showcase-projects';

/**
 * Dựng chuỗi tham số truy vấn.
 *
 * Viết riêng thay vì dùng bản ở module dự án: bản đó chỉ gửi cờ luận lý khi bật, còn ở đây cờ
 * `isPublished` có ba trạng thái — gửi cả `false` để lọc dự án đang ẩn.
 */
function buildQueryString(filter: ShowcaseProjectFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.category !== undefined) {
    parameters.set('category', filter.category.toString());
  }

  if (filter.status !== undefined) {
    parameters.set('status', filter.status.toString());
  }

  if (filter.isPublished !== undefined) {
    parameters.set('isPublished', filter.isPublished ? 'true' : 'false');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', filter.pageNumber.toString());
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', filter.pageSize.toString());
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách dự án tiêu biểu. */
export async function listShowcaseProjects(
  filter: ShowcaseProjectFilter,
): Promise<PagedResponse<ShowcaseProject>> {
  return await request<PagedResponse<ShowcaseProject>>(`${route}${buildQueryString(filter)}`);
}

/** Khai một dự án tiêu biểu mới. */
export async function createShowcaseProject(
  payload: SaveShowcaseProjectPayload,
): Promise<ShowcaseProject> {
  return await request<ShowcaseProject>(route, { method: 'POST', body: payload });
}

/** Sửa toàn bộ nội dung một dự án tiêu biểu. */
export async function updateShowcaseProject(
  id: string,
  payload: SaveShowcaseProjectPayload,
): Promise<ShowcaseProject> {
  return await request<ShowcaseProject>(`${route}/${id}`, { method: 'PUT', body: payload });
}

/** Ẩn hoặc hiện một dự án trên website. */
export async function setShowcaseProjectPublished(
  id: string,
  isPublished: boolean,
): Promise<ShowcaseProject> {
  return await request<ShowcaseProject>(`${route}/${id}/published`, {
    method: 'PUT',
    body: { isPublished },
  });
}

/** Xóa mềm một dự án tiêu biểu. */
export async function deleteShowcaseProject(id: string): Promise<void> {
  await requestNoContent(`${route}/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------------------------
// Ảnh minh họa và trang demo — tệp lưu ở kho công khai (Cloudflare R2), cơ sở dữ liệu chỉ giữ
// đường dẫn. Hai phép kiểm dưới đây chặn sớm ở trình duyệt; máy chủ vẫn kiểm lại.
// ---------------------------------------------------------------------------------------------

/** Đuôi tệp ảnh được nhận. */
export const showcaseImageExtensions = ['.jpg', '.jpeg', '.png', '.webp'] as const;

/** Dung lượng ảnh tối đa: 5 MB — ảnh thẻ trên website không cần lớn hơn. */
export const showcaseImageMaxBytes = 5 * 1024 * 1024;

/** Đuôi tệp demo được nhận: một trang HTML tự chứa. */
export const showcaseDemoExtensions = ['.html', '.htm'] as const;

/** Dung lượng tệp demo tối đa: 10 MB, khớp giới hạn tải lên của máy chủ. */
export const showcaseDemoMaxBytes = 10 * 1024 * 1024;

/** Tải ảnh minh họa lên, thay ảnh cũ nếu có. */
export async function uploadShowcaseProjectImage(id: string, file: File): Promise<ShowcaseProject> {
  const form = new FormData();

  form.append('file', file);

  return await requestForm<ShowcaseProject>(`${route}/${id}/image`, form);
}

/** Gỡ ảnh minh họa — website sẽ dùng ảnh mặc định. */
export async function deleteShowcaseProjectImage(id: string): Promise<ShowcaseProject> {
  return await request<ShowcaseProject>(`${route}/${id}/image`, { method: 'DELETE' });
}

/** Tải tệp demo HTML lên, thay tệp cũ nếu có. */
export async function uploadShowcaseProjectDemo(id: string, file: File): Promise<ShowcaseProject> {
  const form = new FormData();

  form.append('file', file);

  return await requestForm<ShowcaseProject>(`${route}/${id}/demo`, form);
}

/** Gỡ trang demo. */
export async function deleteShowcaseProjectDemo(id: string): Promise<ShowcaseProject> {
  return await request<ShowcaseProject>(`${route}/${id}/demo`, { method: 'DELETE' });
}
